import Text from '@deprecated-ui/common/Text';
import color from '@deprecated-ui/_style/color';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import SecondarySpinner from 'global/Loading/SecondarySpinner';
import VisibilitySensor from 'react-visibility-sensor';
import debounce from 'lodash.debounce';
import { selectors as profileSelector } from 'lib/entities/profile';
import {
  PAGE_SOURCE,
  PAGE_SOURCE_INDEX,
} from 'features/user/analytics/ProfileAnalytics';
import EmptySearch from 'global/StreamWidget/EmptyState/EmptySearch';
import If from 'global/Common/If';
import { ModalFullscreen } from '../styled';
import slice, { effects, selectors } from '../../slice';
import UserItem from './UserItem';
import { ModalFollowSearchInput, UserItemContainer } from './styled';

interface ModalFollowingProps {
  username: string;
  userId: number;
  visible: boolean;
  onCancel: () => void;
  [key: string]: any;
}

const pageSourceIndex = PAGE_SOURCE_INDEX.findIndex(
  (sourceIndex) => sourceIndex === 'Following',
);

function ModalFollowing({
  username,
  userId,
  visible,
  onCancel,
  ...props
}: ModalFollowingProps) {
  const dispatch = useDispatch();
  const visibilityRef = useRef<HTMLDivElement>(null);
  const {
    userFollowing,
    isLoadingFollowing: isLoading,
    followingPagination: pagination,
  } = useSelector(selectors);
  const { more, page, search: keyword } = pagination;
  const { data: dataProfile } = useSelector(profileSelector);

  const [isSensorVisible, setSensorVisible] = useState(false);

  const handleSensorChange = (isVisible = false) => {
    setSensorVisible(isVisible);
  };

  const handleGetFollowings = useCallback(async () => {
    if (!isSensorVisible || !more || isLoading) return;

    dispatch(
      effects.getUserFollowing({
        userid: userId,
        keyword,
        page,
      }),
    );
  }, [isSensorVisible, isLoading, page, more, keyword, userId]);

  useEffect(() => {
    if (visible) {
      handleGetFollowings();
    } else {
      dispatch(slice.actions.resetFollowingState());
    }
  }, [visible, handleGetFollowings]);

  const searchFollowing = useCallback(
    debounce((search?: string) => {
      dispatch(slice.actions.searchFollowing(search));
      setSensorVisible(true);
    }, 500),
    [],
  );

  const followings = userFollowing[userId] || [];

  if (!visible) return null;

  return (
    <ModalFullscreen
      {...props}
      // @ts-expect-error
      noBorder
      visible={visible}
      onCancel={onCancel}
      footer={null}
      type="fullscreen"
      maskClosable={false}
      transitionName=""
      maskTransitionName=""
      title={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <Text size="20px" family="bold" align="center" color={color.suvaGray}>
          {username} is following
        </Text>
      }
    >
      <ModalFollowSearchInput
        placeholder="Search"
        onChange={({ target: { value } }) =>
          searchFollowing(value || undefined)
        }
        onClear={() => dispatch(slice.actions.searchFollowing())}
      />
      <UserItemContainer ref={visibilityRef}>
        {followings.map((following) => (
          <UserItem
            pageSource={PAGE_SOURCE.following}
            pageSourceIndex={pageSourceIndex}
            user={following}
            key={following.id}
            activeUserId={Number(dataProfile?.id)}
          />
        ))}
        <SecondarySpinner isLoading={isLoading} />
        <VisibilitySensor
          containment={visibilityRef.current}
          onChange={handleSensorChange}
          scrollCheck
        >
          <div>&nbsp;</div>
        </VisibilitySensor>
        <If condition={followings.length === 0 && !isLoading}>
          <EmptySearch />
        </If>
      </UserItemContainer>
    </ModalFullscreen>
  );
}

export default ModalFollowing;
